import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Row, Card } from 'reactstrap';
import { SkeletonTable } from '../../../../../../../../../Components/Common/Skeleton';
import { getAuthToken } from '../../../../../../../../../auth/tokenvalidation';
import {
  getVisibleProcurementClauses,
  TYPE_RESPONSE_COUNTER_PAYMENT_TERM,
  TYPE_RESPONSE_COUNTER_COMPLIANCE,
  hasBidderConfirmedRow,
  TYPE_RESPONSE_COUNTER_DELAY_PENALTY_TERM,
} from '../../../../../../../../../shared/offer-domain/respond';
import { requiresLpgCargoRatioForCargo } from '../../../../../../../../../shared/offer-domain/constants';
import {
  enrichLpgDeliveryItemsWithCargoRatio,
  enrichDeliveryItemsWithCounterOffers,
  enrichDeliveryItemsWithFobLocations,
  ParcelDeliveryPricingTabs,
} from '../../../../../../../../../shared/offer-domain/delivery';
import {
  SectionHeader,
  SectionCard,
} from '../../../../../../../../../shared/offer-domain/ui';
import DeliveryDetailTableLpg from './DeliveryDetailTableLpg';
import ClauseCard from '../shared/ClauseCard';
import OfferPatternSections from '../shared/OfferPatternSections';
import PaymentComplianceView from '../../../../../Clarification/ClarificationDetail/components/PaymentComplianceView';

const DELAY_PENALTY_RESPOND_KEY = TYPE_RESPONSE_COUNTER_DELAY_PENALTY_TERM;
const PAYMENT_RESPOND_KEY = TYPE_RESPONSE_COUNTER_PAYMENT_TERM;
const COMPLIANCE_RESPOND_KEY = TYPE_RESPONSE_COUNTER_COMPLIANCE;

const RequirementTabLpg = ({
  form,
  procurement,
  procurementMitraId = null,
  qtyDeliveryData,
  clauseResponses = {},
  deliveryItemsByTermId = {},
  typeResponses = {},
  isLoading = false,
  error = null,
}) => {
  // Cargo ratio follows the product type of each cargo (quantity delivery term)
  const requiresCargoRatioByTermId = useMemo(() => {
    const map = {};
    (qtyDeliveryData?.items ?? []).forEach(item => {
      map[item.id] = requiresLpgCargoRatioForCargo(form, item);
    });
    return map;
  }, [form, qtyDeliveryData]);
  const requiresCargoRatioFor = useCallback(
    termId =>
      requiresCargoRatioByTermId[termId] ??
      requiresLpgCargoRatioForCargo(form, null),
    [requiresCargoRatioByTermId, form]
  );
  const visibleClauses = getVisibleProcurementClauses({
    procurementClauseDetails: form?.procurementClauseDetails,
  });

  const [enrichedByTermId, setEnrichedByTermId] = useState(
    deliveryItemsByTermId
  );
  const [isEnriching, setIsEnriching] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const enrichAll = async () => {
      const termEntries = Object.entries(deliveryItemsByTermId || {});
      if (!termEntries.length) {
        setEnrichedByTermId({});
        return;
      }

      const procurementId = form?.id;
      const token = getAuthToken();
      if (!procurementId) {
        setEnrichedByTermId(deliveryItemsByTermId);
        return;
      }

      setIsEnriching(true);
      try {
        const nextMap = {};
        for (const [termId, items] of termEntries) {
          let nextItems = items ?? [];
          const hasCargoRatio = nextItems.some(
            item => item.cargoRatio || item.cargoRatioLabel
          );

          try {
            if (!hasCargoRatio) {
              nextItems = await enrichLpgDeliveryItemsWithCargoRatio({
                procurementId,
                quantityDeliveryTermId: termId,
                items: nextItems,
                skipCargoRatio: !requiresCargoRatioFor(termId),
                // be#456: Alpha cargo ratio milik bidder modal ini.
                procurementMitraId,
              });
            }
          } catch (enrichError) {
            // keep base items when cargo enrichment fails
          }

          if (token) {
            try {
              nextItems = await enrichDeliveryItemsWithFobLocations({
                quantityDeliveryTermId: termId,
                token,
                items: nextItems,
              });
            } catch (enrichError) {
              // keep items when FOB enrichment fails
            }
            try {
              nextItems = await enrichDeliveryItemsWithCounterOffers({
                quantityDeliveryTermId: termId,
                token,
                items: nextItems,
                requireIncotermCounter: true,
              });
            } catch (enrichError) {
              // keep items when counter enrichment fails
            }
          }

          nextMap[termId] = nextItems;
        }

        if (!cancelled) setEnrichedByTermId(nextMap);
      } finally {
        if (!cancelled) setIsEnriching(false);
      }
    };

    enrichAll();
    return () => {
      cancelled = true;
    };
  }, [
    deliveryItemsByTermId,
    form?.id,
    requiresCargoRatioFor,
    procurementMitraId,
  ]);

  const showLoading = isLoading || isEnriching;

  return (
    <>
      <SectionCard>
        <SectionHeader
          title='Offering'
          subtitle='Review delivery detail and clause responses submitted by the bidder.'
        />
        {showLoading ? (
          <SkeletonTable rows={4} columns={7} />
        ) : error ? (
          <div className='alert alert-danger text-center mb-0'>{error}</div>
        ) : qtyDeliveryData?.items?.length > 0 ? (
          <div className='d-flex flex-column gap-4'>
            {qtyDeliveryData.items.map((data, index) => (
              <Card
                className='border shadow-sm mb-0 overflow-hidden'
                key={`${data.id}-${index}`}
              >
                <div className='bg-light border-bottom px-4 py-3'>
                  <h6 className='fw-semibold text-dark mb-0'>
                    Delivery Unit:{' '}
                    {data.deliveryUnit ||
                      `Cargo ${String.fromCharCode(65 + index)}`}
                  </h6>
                </div>
                <ParcelDeliveryPricingTabs
                  parcel={data}
                  deliveryContent={
                    <DeliveryDetailTableLpg
                      items={enrichedByTermId?.[data.id] ?? []}
                      requiresCargoRatio={requiresCargoRatioFor(data.id)}
                    />
                  }
                />
              </Card>
            ))}
          </div>
        ) : (
          <div className='text-center py-5 text-muted border rounded-3 bg-light-subtle'>
            No data available
          </div>
        )}
      </SectionCard>

      <OfferPatternSections
        procurement={procurement}
        typeResponses={typeResponses}
      />

      <SectionCard>
        <SectionHeader
          title='Clause Details'
          countLabel={`${visibleClauses.length} clauses`}
        />
        {visibleClauses.length > 0 ? (
          <Row>
            {visibleClauses.map((clause, index) => (
              <ClauseCard
                key={clause.id}
                index={index + 1}
                clause={clause}
                clauseResponses={clauseResponses}
              />
            ))}
          </Row>
        ) : (
          <div className='text-center py-4 text-muted border rounded-3 bg-light-subtle'>
            No clause data available.
          </div>
        )}
      </SectionCard>

      <PaymentComplianceView
        procurement={procurement}
        procurementMitraId={procurementMitraId}
        isDelayPenaltyConfirmed={hasBidderConfirmedRow(
          typeResponses[DELAY_PENALTY_RESPOND_KEY]
        )}
        delayPenaltyRespond={
          <Row>
            <ClauseCard
              clause={{
                id: DELAY_PENALTY_RESPOND_KEY,
                clauseTitle: 'Delay Penalty Terms Clarification',
                clauseDescription: '',
              }}
              clauseResponses={{
                [DELAY_PENALTY_RESPOND_KEY]:
                  typeResponses[DELAY_PENALTY_RESPOND_KEY] ?? [],
              }}
            />
          </Row>
        }
        paymentRespond={
          <Row>
            <ClauseCard
              clause={{
                id: PAYMENT_RESPOND_KEY,
                clauseTitle: 'Payment Terms Clarification',
                clauseDescription: '',
              }}
              clauseResponses={{
                [PAYMENT_RESPOND_KEY]: typeResponses[PAYMENT_RESPOND_KEY] ?? [],
              }}
            />
          </Row>
        }
        complianceRespond={
          <Row>
            <ClauseCard
              clause={{
                id: COMPLIANCE_RESPOND_KEY,
                clauseTitle: 'Compliance Clarification',
                clauseDescription: '',
              }}
              clauseResponses={{
                [COMPLIANCE_RESPOND_KEY]:
                  typeResponses[COMPLIANCE_RESPOND_KEY] ?? [],
              }}
            />
          </Row>
        }
      />
    </>
  );
};

export default RequirementTabLpg;
